import { useContext } from 'react'
import cn from 'classnames'
import { Link, type LinkProps } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'

import BrandingContext, { type TBranding } from '../context/BrandingContext'

type TStep = {
  id: string
  name: string
  desc: string
  to: LinkProps['to']
  current: boolean
  complete: boolean
}

type TStepsBarItemProps = {
  step: TStep
  stepCount: number
  stepIndex: number
}

export type TStepsBarProps = {
  steps: TStep[]
}

const StepsBarItem = (props: TStepsBarItemProps) => {
  const { step, stepCount, stepIndex } = props
  const [branding] = useContext(BrandingContext)
  const { primaryColorPalette }: Pick<TBranding, 'primaryColorPalette'> =
    branding

  const containerCn = cn('border border-gray-200 overflow-hidden md:border-0', {
    'border-b-0': stepIndex === 0,
    'border-t-0': stepIndex === stepCount - 1,
  })

  const stepCn = cn('px-6 py-4 flex items-start text-sm font-medium', {
    'md:pl-9': stepIndex !== 0,
  })

  const indicatorCn = cn(
    'absolute top-0 left-0 w-1 h-full md:w-full md:h-1 md:bottom-0 md:top-auto',
    {
      'bg-transparent group-hover:bg-gray-200': !step.current,
      [`bg-${primaryColorPalette}-600`]: step.current,
    },
  )

  if (step.complete) {
    return (
      <li key={step.id} className="relative overflow-hidden md:flex-1">
        <div className={containerCn}>
          <Link to={step.to} className="group">
            <span className={indicatorCn} aria-hidden="true" />
            <span className={stepCn}>
              <span className="shrink-0">
                <span
                  className={`w-10 h-10 flex items-center justify-center bg-${primaryColorPalette}-600 rounded-full`}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="w-6 h-6 text-white"
                    aria-hidden="true"
                  />
                </span>
              </span>
              <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
                <span className="text-xs font-semibold tracking-wide uppercase">
                  {step.name}
                </span>
                <span className="text-sm font-normal text-gray-600">
                  {step.desc}
                </span>
              </span>
            </span>
          </Link>
          {stepIndex !== 0 && (
            <div
              className="hidden absolute top-0 left-0 w-3 inset-0 md:block"
              aria-hidden="true"
            >
              <svg
                className="h-full w-full text-gray-300"
                viewBox="0 0 12 82"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M0.5 0V31L10.5 41L0.5 51V82"
                  stroke="currentcolor"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </div>
          )}
        </div>
      </li>
    )
  }

  if (step.current) {
    return (
      <li key={step.id} className="relative overflow-hidden md:flex-1">
        <div className={containerCn}>
          <span className={indicatorCn} aria-hidden="true" />
          <span className={stepCn}>
            <span className="shrink-0">
              <span
                className={`w-10 h-10 flex items-center justify-center border-2 border-${primaryColorPalette}-600 rounded-full`}
              >
                <span className={`text-${primaryColorPalette}-600`}>
                  0{stepIndex + 1}
                </span>
              </span>
            </span>
            <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
              <span
                className={`text-xs font-semibold text-${primaryColorPalette}-600 tracking-wide uppercase`}
              >
                {step.name}
              </span>
              <span className="text-sm font-normal text-gray-600">
                {step.desc}
              </span>
            </span>
          </span>
          {stepIndex !== 0 && (
            <div
              className="hidden absolute top-0 left-0 w-3 inset-0 md:block"
              aria-hidden="true"
            >
              <svg
                className="h-full w-full text-gray-300"
                viewBox="0 0 12 82"
                fill="none"
                preserveAspectRatio="none"
              >
                <path
                  d="M0.5 0V31L10.5 41L0.5 51V82"
                  stroke="currentcolor"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            </div>
          )}
        </div>
      </li>
    )
  }

  return (
    <li key={step.id} className="relative overflow-hidden md:flex-1">
      <div className={containerCn}>
        <span className={indicatorCn} aria-hidden="true" />
        <span className={stepCn}>
          <span className="shrink-0">
            <span className="w-10 h-10 flex items-center justify-center border-2 border-gray-300 rounded-full">
              <span className="text-gray-400">0{stepIndex + 1}</span>
            </span>
          </span>
          <span className="mt-0.5 ml-4 min-w-0 flex flex-col">
            <span className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
              {step.name}
            </span>
            <span className="text-sm font-normal text-gray-500">
              {step.desc}
            </span>
          </span>
        </span>
        {stepIndex !== 0 && (
          <div
            className="hidden absolute top-0 left-0 w-3 inset-0 md:block"
            aria-hidden="true"
          >
            <svg
              className="h-full w-full text-gray-300"
              viewBox="0 0 12 82"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M0.5 0V31L10.5 41L0.5 51V82"
                stroke="currentcolor"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
        )}
      </div>
    </li>
  )
}

const StepsBar = (props: TStepsBarProps) => {
  const { steps } = props

  return (
    <div className="md:border-b md:border-gray-200">
      <nav className="mx-auto max-w-5xl" aria-label="Progress">
        <ol className="md:flex md:border-t md:border-x md:border-gray-200 md:rounded-t-lg">
          {steps.map((step: TStep, index: number) => (
            <StepsBarItem
              key={step.name}
              step={step}
              stepIndex={index}
              stepCount={steps.length}
            />
          ))}
        </ol>
      </nav>
    </div>
  )
}

export default StepsBar
